import React, { useEffect, useState } from 'react';
import { X, PhoneCall, MessageCircle, UserPlus } from 'lucide-react';
import { FootballPosition, GroupMemberProfile, Match } from '../types';

interface JoinMatchRequestPayload {
  requesterType: 'member' | 'guest';
  requesterName: string;
  phoneNumber: string;
  whatsappNumber?: string;
  preferredPosition?: FootballPosition;
  note?: string;
}

interface JoinMatchRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: Match | null;
  activeMember: GroupMemberProfile;
  onSubmit: (payload: JoinMatchRequestPayload) => void;
}

const POSITIONS: FootballPosition[] = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'ST'];

export const JoinMatchRequestModal: React.FC<JoinMatchRequestModalProps> = ({
  isOpen,
  onClose,
  match,
  activeMember,
  onSubmit,
}) => {
  const [requesterType, setRequesterType] = useState<'member' | 'guest'>('member');
  const [requesterName, setRequesterName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [preferredPosition, setPreferredPosition] = useState<FootballPosition>('CM');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setRequesterType('member');
    setRequesterName(activeMember.displayName || '');
    setPhoneNumber(activeMember.phoneNumber || '');
    setWhatsappNumber(activeMember.whatsappNumber || activeMember.phoneNumber || '');
    setPreferredPosition(activeMember.primaryPosition || 'CM');
    setNote('');
  }, [isOpen, activeMember]);

  if (!isOpen || !match) return null;

  const isGuest = requesterType === 'guest';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requesterName.trim() || !phoneNumber.trim()) return;
    onSubmit({
      requesterType,
      requesterName: requesterName.trim(),
      phoneNumber: phoneNumber.trim(),
      whatsappNumber: whatsappNumber.trim() || undefined,
      preferredPosition,
      note: note.trim() || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-950 shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 border-b border-white/10 p-5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">Join This Match</div>
            <h3 className="mt-1 text-lg font-black text-white">{match.title}</h3>
            <p className="text-xs text-slate-400 mt-1">Send a player request or a guest lead straight to the organizer.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 p-2 text-slate-400 hover:text-white hover:border-white/20 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-white/5 p-2">
            <button
              type="button"
              onClick={() => {
                setRequesterType('member');
                setRequesterName(activeMember.displayName || '');
                setPhoneNumber(activeMember.phoneNumber || '');
                setWhatsappNumber(activeMember.whatsappNumber || activeMember.phoneNumber || '');
                setPreferredPosition(activeMember.primaryPosition || 'CM');
              }}
              className={`rounded-xl px-3 py-3 text-xs font-black transition ${!isGuest ? 'bg-emerald-400 text-slate-950' : 'text-slate-300 hover:bg-white/5'}`}
            >
              Join as me
            </button>
            <button
              type="button"
              onClick={() => {
                setRequesterType('guest');
                setRequesterName('');
                setPhoneNumber('');
                setWhatsappNumber('');
                setPreferredPosition('CM');
              }}
              className={`rounded-xl px-3 py-3 text-xs font-black transition ${isGuest ? 'bg-cyan-400 text-slate-950' : 'text-slate-300 hover:bg-white/5'}`}
            >
              Request guest spot
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">Player name</label>
              <input
                type="text"
                value={requesterName}
                onChange={(e) => setRequesterName(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-400"
                placeholder={isGuest ? 'Guest player name' : 'Your name'}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">Preferred position</label>
              <select
                value={preferredPosition}
                onChange={(e) => setPreferredPosition(e.target.value as FootballPosition)}
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-400"
              >
                {POSITIONS.map((position) => (
                  <option key={position} value={position}>{position}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-bold text-slate-300"><PhoneCall className="w-3.5 h-3.5" /> Mobile number</label>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-400"
                placeholder="01XXXXXXXXX"
                required
              />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-bold text-slate-300"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp number</label>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-400"
                placeholder="Optional"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 mb-1">Note to organizer</label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:outline-none focus:border-emerald-400"
              placeholder="Example: Can play CM/RB, can confirm payment early, bringing one friend, etc."
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-xs text-slate-300">
            <div className="font-bold text-white mb-1">How this works</div>
            <ul className="space-y-1 list-disc pl-4">
              <li>Open Join + approved player: joins instantly.</li>
              <li>Approval Required: organizer receives a request to approve.</li>
              <li>Guest requests always keep organizer contact info attached for manual confirmation.</li>
            </ul>
          </div>

          <button type="submit" className="w-full rounded-2xl bg-emerald-400 px-4 py-3 text-sm font-black text-slate-950 hover:bg-emerald-300 transition flex items-center justify-center gap-2">
            <UserPlus className="w-4 h-4" />
            {match.joinPolicy === 'open_join' && !isGuest ? 'Join instantly' : 'Send join request'}
          </button>
        </form>
      </div>
    </div>
  );
};
